import React, { useState, useRef, useEffect } from 'react';
import { 
  GiMusicalNotes, 
  GiPlayButton, 
  GiPauseButton,
  GiSpeaker
} from 'react-icons/gi';
import { useLocation } from 'react-router-dom';
import MusicModal from './MusicModal';
import './MusicPlayer.css';

const DEFAULT_TRACKS = [
      {
    id: 'grey_moon',
    title: 'Grey Moon',
    url: 'https://om-assets.fra1.cdn.digitaloceanspaces.com/assets/audio/background_music/grey_moon.mp3'
  },
    {
    id: 'vicious_approach',
    title: 'Vicious Approach',
    url: 'https://om-assets.fra1.cdn.digitaloceanspaces.com/assets/audio/background_music/viscious_approach.mp3'
  },
  {
    id: 'witching_hour',
    title: 'Witching Hour',
    url: 'https://om-assets.fra1.cdn.digitaloceanspaces.com/assets/audio/background_music/witching_hour.mp3'
  },
  {
    id: 'awaken_the_mists',
    title: 'Awaken the Mists',
    url: 'https://om-assets.fra1.cdn.digitaloceanspaces.com/assets/audio/background_music/awaken_the_mists.mp3'
  },
  {
    id: 'stone_and_dust',
    title: 'Stone and Dust',
    url: 'https://om-assets.fra1.cdn.digitaloceanspaces.com/assets/audio/background_music/stone_and_dust.mp3'
  },
  {
    id: 'gems_and_bones',
    title: 'Gems and Bones',
    url: 'https://om-assets.fra1.cdn.digitaloceanspaces.com/assets/audio/background_music/gems_and_bones.mp3'
  },
  {
    id: 'a_suspicious_chest',
    title: 'A Suspicious Chest',
    url: 'https://om-assets.fra1.cdn.digitaloceanspaces.com/assets/audio/background_music/a_suspicious_chest.mp3'
  },
  {
    id: 'treasure_awaits',
    title: 'Treasure Awaits',
    url: 'https://om-assets.fra1.cdn.digitaloceanspaces.com/assets/audio/background_music/treasure_awaits.mp3'
  }
];


const MusicPlayer = () => {
  const [showModal, setShowModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(DEFAULT_TRACKS[0]);
  const [volume, setVolume] = useState(0.5);
  const audioRef = useRef(new Audio(DEFAULT_TRACKS[0].url));
  const location = useLocation();

  // Updated to include bookshelf route
  const showMusicPlayer = location.pathname.includes('story') || 
                         location.pathname.includes('travel') ||
                         location.pathname === '/table' ||
                         location.pathname === '/bookshelf' ||
                         location.pathname.includes('/bookshelf');

  // Initialize audio element
  useEffect(() => {
    audioRef.current.loop = true;
    audioRef.current.volume = volume;
    
    return () => {
      audioRef.current.pause();
      audioRef.current.src = '';
    };
  }, []);

  // Handle track changes
  useEffect(() => {
    if (currentTrack) {
      const wasPlaying = isPlaying;
      audioRef.current.pause();
      audioRef.current.src = currentTrack.url;
      audioRef.current.load();
      
      if (wasPlaying) {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(err => {
            console.error("Error playing track:", err);
            setIsPlaying(false);
          });
      }
    }
  }, [currentTrack]);

  // Handle volume changes
  useEffect(() => {
    audioRef.current.volume = volume;
  }, [volume]);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(err => {
          console.error("Error playing track:", err);
          setIsPlaying(false);
        });
    }
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  // New function to handle clicking on a track - selects AND plays
  const handleTrackClick = (track) => {
    // If clicking the current track, just toggle play state
    if (track.id === currentTrack.id) {
      togglePlay();
    } else {
      // If clicking a different track, select it and ensure it starts playing
      setCurrentTrack(track);
      // Start playing after a small delay to allow the track to load
      setTimeout(() => {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(err => {
            console.error("Error playing track:", err);
            setIsPlaying(false);
          });
      }, 50);
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
  };

  if (!showMusicPlayer) return null;

  return (
    <>
      <button 
        className={`music-player-button ${isPlaying ? 'playing' : ''}`}
        onClick={toggleModal}
        title="Music Player"
      >
        <GiMusicalNotes />
        <span>Music</span>
      </button>
      
      <MusicModal 
        isOpen={showModal}
        onClose={toggleModal}
        tracks={DEFAULT_TRACKS}
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        volume={volume}
        onSelectTrack={handleTrackClick} // Now using the new combined function
        onTogglePlay={togglePlay}
        onVolumeChange={handleVolumeChange}
      />
    </>
  );
};

export default MusicPlayer;